import { ActionMode, GeneralStatusEnum, KindergartenDetailsTabs } from '../../../common/enums/common.enum';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import { createNotification, getStatusColor } from '../../../utilities/helper/notification.helper';
import { CustomErrorStatus, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createChild, fetchParentsBasicInfo, getChildDetails, getLocationsWIthGroups, updateChild } from '../../../api/kindergarten.api';
import { ChildDto, CreateChildDto, LocationDto, UpdateChildDto, UserBasicInfo } from '../../../common/interfaces/kindergarten.interface';
import GenericLocationGroupSelector from '../../../components/GenericLocationGroupSelector/GenericLocationGroupSelector';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as CircleIcon } from '../../../assets/circle-icon.svg';
import GenericPeopleDropdown from '../../../components/GenericPeopleDropdown';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { ProfilePageBox, ProfilePictureAvatar } from '../../../common/styles/Common.styles';

interface TeacherPageProps {
  mode: ActionMode;
}

const ChildPage: React.FC<TeacherPageProps> = ({ mode }) => {
  const { childId, kindergartenId, kindergartenName } = useParams<{
    childId?: string;
    kindergartenId: string;
    kindergartenName: string;
  }>();

  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [status, setStatus] = useState<GeneralStatusEnum>(GeneralStatusEnum.ACTIVE);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [childGroup, setChildGroup] = useState<string>('');
  const [parents, setParents] = useState<UserBasicInfo[]>([]);
  const [parentOptions, setParentOptions] = useState<UserBasicInfo[]>();
  const [initialChild, setInitialChild] = useState<ChildDto>();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pendingParents, setPendingParents] = useState<UserBasicInfo[]>([]);
  const [isEditing, setIsEditing] = useState(mode === ActionMode.CREATE || mode === ActionMode.EDIT);
  const [currentMode, setCurrentMode] = useState<ActionMode>(mode);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  useEffect(() => {
    getParents();
    if (currentMode === ActionMode.CREATE) {
      fetchLocationsWithGroup();
    }
  }, [kindergartenId]);

  useEffect(() => {
    if (parentOptions) {
      if (currentMode === ActionMode.EDIT || (currentMode === ActionMode.DETAILS && childId)) {
        fetchChildDetails();
      }
    }
  }, [kindergartenId, mode, childId, parentOptions]);

  const handleSave = async () => {
    const success = await saveChild();
    if (success) {
      navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Children}`);
    }
  };

  const getParents = async () => {
    try {
      if (!kindergartenId) return;
      const response = await fetchParentsBasicInfo(kindergartenId);
      setParentOptions(response.results ?? []);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch parents.');
    }
  };

  const handleSaveAndCreateAnother = async () => {
    const success = await saveChild();
    if (success) {
      setFirstName('');
      setMiddleName('');
      setLastName('');
      setStatus(GeneralStatusEnum.ACTIVE);
      setBirthday('');
      setChildGroup('');
      setParents([]);
    }
  };
  const saveChild = async () => {
    if (!kindergartenId) return;

    if (!firstName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, 'First name is required');
      return false;
    }
    if (!lastName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, 'Last name is required');
      return false;
    }
    if (!middleName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, 'Middle name is required');
      return false;
    }
    if (!birthday.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, 'Birthday is required');
      return false;
    }

    try {
      if (currentMode === ActionMode.CREATE) {
        const childPayload: CreateChildDto = {
          firstName: firstName,
          middleName: middleName,
          lastName: lastName,
          status: status,
          birthdate: birthday
        };
        if (childGroup) {
          childPayload.groupId = childGroup;
        }

        if (parents.length > 0) {
          childPayload.parentIds = parents.map(p => p.id);
        }

        const response = await createChild(kindergartenId, childPayload);
        createNotification(MESSAGE_TYPE.SUCCESS, response?.message ? response.message : 'Child created successfully');
      } else if (currentMode === ActionMode.EDIT && childId) {
        const childPayload: UpdateChildDto = {};
        if (firstName !== initialChild?.firstName) childPayload.firstName = firstName;
        if (lastName !== initialChild?.lastName) childPayload.lastName = lastName;
        if (middleName !== initialChild?.middleName) childPayload.middleName = middleName;
        if (status !== initialChild?.status) childPayload.status = status;
        if (birthday !== initialChild?.birtDate) childPayload.birthdate = birthday;

        const initialGroupId =
          initialChild?.locations?.flatMap(loc => loc.groups?.filter(g => g.assigned).map(g => g.id) ?? []).find(id => !!id) ?? '';

        if (childGroup !== initialGroupId) {
          childPayload.groupId = !childGroup ? '' : childGroup;
        }

        const initialParentIds = (initialChild?.parents ?? []).map(p => p.id) || [];
        const currentParentIds = parents.map(p => p.id);

        const parentsChanged =
          initialParentIds.length !== currentParentIds.length || !initialParentIds.every(id => currentParentIds.includes(id));

        if (parentsChanged) {
          childPayload.parentIds = currentParentIds;
        }
        if (Object.keys(childPayload).length > 0) {
          const response = await updateChild(kindergartenId, childId, childPayload);
          createNotification(MESSAGE_TYPE.SUCCESS, response?.message ?? 'Child updated successfully');
        }
      }
      return true;
    } catch (error: any) {
      const backendStatus = error?.response?.data?.status;
      if (backendStatus === CustomErrorStatus.CHILD_ALREADY_EXISTS) {
        createNotification(MESSAGE_TYPE.ERROR, 'Child with provided data already exists in this kindergarten');
      } else if (backendStatus === CustomErrorStatus.CHILD_NOT_ACTIVE) {
        createNotification(MESSAGE_TYPE.ERROR, 'An inactive child cannot be assigned to a group');
      } else {
        createNotification(MESSAGE_TYPE.ERROR, error?.response?.message ? error?.response.message : 'Failed to create child');
      }
      return false;
    }
  };
  const fetchLocationsWithGroup = async () => {
    try {
      if (!kindergartenId) return;
      setIsLoadingLocations(true);
      const result = await getLocationsWIthGroups(kindergartenId, GeneralStatusEnum.ACTIVE);
      if (result.results) {
        setLocations(result.results);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch locations with groups');
    } finally {
      setIsLoadingLocations(false);
    }
  };
  const fetchChildDetails = async () => {
    if (!kindergartenId || !childId) return;
    try {
      setIsLoadingLocations(true);
      const response = await getChildDetails(kindergartenId, childId);
      if (response.results) {
        const child = response.results;
        setInitialChild(child);
        setFirstName(child.firstName);
        setLastName(child.lastName);
        setMiddleName(child.middleName);
        setStatus(child.status);
        setBirthday(child.birtDate);

        setLocations(child.locations ?? []);
        const assignedGroupId =
          child.locations
            ?.flatMap(loc => loc?.groups?.filter(g => g.assigned)?.map(g => g.id) ?? [])
            .filter((id): id is string => !!id)[0] ?? '';
        setChildGroup(assignedGroupId);

        if (child.parents?.length) {
          const mappedParents = child.parents.map(p => parentOptions?.find(po => po.id === p.id) || p);
          setParents(mappedParents);
        } else {
          setParents([]);
        }
        setAvatarUrl(child.profileImage ?? null);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch child details');
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const handleParentsChange = (newParents: UserBasicInfo[]) => {
    const removed = parents.filter(p => !newParents.some(np => np.id === p.id));
    if (removed.length > 0) {
      setPendingParents(newParents);
      setOpenConfirmModal(true);
    } else {
      setParents(newParents);
    }
  };

  const confirmRemoveParents = () => {
    setParents(pendingParents);
    setPendingParents([]);
    setOpenConfirmModal(false);
  };

  const cancelRemoveParents = () => {
    setPendingParents([]);
    setOpenConfirmModal(false);
  };

  const getPageTitle = () => {
    switch (currentMode) {
      case ActionMode.CREATE:
        return 'Create child';
      case ActionMode.EDIT:
        return 'Edit child';
      case ActionMode.DETAILS:
        return `${firstName} (${middleName}) ${lastName}`;
    }
  };

  const isFormValid = useMemo(() => {
    return Boolean(firstName.trim() && middleName.trim() && lastName.trim() && birthday.trim());
  }, [firstName, middleName, lastName, birthday]);

  return (
    <Box p={7}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          Kindergartens
        </Link>
        {kindergartenName && (
          <Link
            color="text.secondary"
            underline="hover"
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Children}`)}
            sx={{ cursor: 'pointer' }}
          >
            {kindergartenName}
          </Link>
        )}
        <Typography color="text.primary">{getPageTitle()}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="44px">
        <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {currentMode === ActionMode.DETAILS && (
            <CircleIcon
              style={{
                color: getStatusColor(status),
                fontSize: 12
              }}
            />
          )}
          {currentMode === ActionMode.CREATE && 'Create child'}
          {currentMode === ActionMode.EDIT && 'Edit child'}
          {currentMode === ActionMode.DETAILS && `${firstName} (${middleName}) ${lastName}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {mode === ActionMode.CREATE && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              variant="outlined"
              onClick={handleSaveAndCreateAnother}
              label="Save & create another"
              disabled={!isFormValid}
            />
          )}
          {isEditing && currentMode !== ActionMode.DETAILS && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={handleSave}
              label="Save"
              disabled={!isFormValid}
            />
          )}
          {currentMode === ActionMode.DETAILS && !isEditing && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={() => {
                setIsEditing(true);
                setCurrentMode(ActionMode.EDIT);
              }}
              label="Edit"
              startIcon={<EditIcon />}
            />
          )}
        </Box>
      </Box>

      <ProfilePageBox>
        {(mode === ActionMode.EDIT || mode === ActionMode.DETAILS) && (
          <ProfilePictureAvatar src={avatarUrl ?? undefined} alt={`${firstName} ${lastName}`}></ProfilePictureAvatar>
        )}
        <Box
          sx={{
            display: 'flex',
            width: '500px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '20px'
          }}
        >
          <GenericTextField
            label="First name"
            required={true}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label="Middle name"
            required={true}
            value={middleName}
            onChange={e => setMiddleName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label="Last name"
            required={true}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label="Birthday"
            required={true}
            isDate={true}
            value={birthday}
            onChange={e => setBirthday(e.target.value)}
            disabled={!isEditing}
          />
          <GenericStatusSwitch label="Child status" status={status} onChange={newStatus => setStatus(newStatus)} disabled={!isEditing} />
          <Typography variant="caption"> Assign to group </Typography>
          {kindergartenId && (
            <GenericLocationGroupSelector
              kindergartenId={kindergartenId}
              locations={locations}
              selectedGroups={[childGroup]}
              onChange={group => setChildGroup(group[0])}
              disabled={!isEditing || status === GeneralStatusEnum.INACTIVE}
              exclusive={true}
              isLoading={isLoadingLocations}
            />
          )}
          {parentOptions && (
            <GenericPeopleDropdown
              label="Parents"
              options={parentOptions}
              selectedOptions={parents}
              onChange={handleParentsChange}
              disabled={!isEditing}
            />
          )}
        </Box>
      </ProfilePageBox>
      <GenericInfoModal
        open={openConfirmModal}
        onClose={cancelRemoveParents}
        onConfirm={confirmRemoveParents}
        label="Remove parent(s)"
        message={`Are you sure you want to remove parent from ${firstName} ${middleName} ${lastName}`}
        confirmLabel="Remove"
        entityName=""
      />
    </Box>
  );
};
export default ChildPage;
